import { SiteHeader } from "@/components/site-header"
import { Link } from "react-router-dom"
import { ArrowLeftIcon } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

// ─── Column definitions ───────────────────────────────────────────────────────

type ColKind = "crt" | "detail" | "ignored" | "empty"

interface Col {
  idx:    number
  name:   string
  kind:   ColKind
  field?: string     // DB column name
  notes?: string
}

const COLUMNS: Col[] = [
  { idx: 0,  name: "(vacío)",                      kind: "empty" },
  { idx: 1,  name: "(vacío)",                      kind: "empty" },
  { idx: 2,  name: "CRT",                          kind: "crt",    field: "crt" },
  { idx: 3,  name: "Tipo de Operação",              kind: "ignored" },
  { idx: 4,  name: "Fronteira de Cruze",            kind: "crt",    field: "border_crossing" },
  { idx: 5,  name: "ID",                            kind: "detail", field: "source_id" },
  { idx: 6,  name: "Nome Motorista",                kind: "detail", field: "driver_name" },
  { idx: 7,  name: "Placa Cavalo",                  kind: "detail", field: "truck_plate" },
  { idx: 8,  name: "Proprietário Cavalo",           kind: "detail", field: "truck_owner" },
  { idx: 9,  name: "Posicionamento Carga",          kind: "detail", field: "cargo_position" },
  { idx: 10, name: "Local Remetente",               kind: "crt",    field: "shipper_location" },
  { idx: 11, name: "Cidade Remetente",              kind: "crt",    field: "shipper_city" },
  { idx: 12, name: "País Remetente",                kind: "crt",    field: "shipper_country" },
  { idx: 13, name: "Local Destinatário",            kind: "crt",    field: "recipient_location" },
  { idx: 14, name: "RG / C.I Motorista",            kind: "ignored", notes: "Nuevo en v2 — sin campo en DB aún" },
  { idx: 15, name: "Cidade Destinatário",           kind: "crt",    field: "recipient_city" },
  { idx: 16, name: "País Destinatário",             kind: "crt",    field: "recipient_country" },
  { idx: 17, name: "Placa Transbordo",              kind: "ignored" },
  { idx: 18, name: "Carreta Transbordo",            kind: "ignored" },
  { idx: 19, name: "Bitrem Transbordo",             kind: "ignored" },
  { idx: 20, name: "Tipo Carreta",                  kind: "ignored" },
  { idx: 21, name: "Rastreador Cavalo",             kind: "ignored" },
  { idx: 22, name: "Moeda",                         kind: "crt",    field: "currency" },
  { idx: 23, name: "Frete",                         kind: "crt",    field: "freight" },
  { idx: 24, name: "Peso",                          kind: "detail", field: "weight" },
  { idx: 25, name: "Fone Motorista",                kind: "detail", field: "driver_phone" },
  { idx: 26, name: "Lacre",                         kind: "crt",    field: "seal" },
  { idx: 27, name: "Cliente",                       kind: "crt",    field: "client" },
  { idx: 28, name: "Cliente(Nome Simples)",          kind: "crt",    field: "client_simple_name" },
  { idx: 29, name: "Produto",                       kind: "crt",    field: "product" },
  { idx: 30, name: "Transportadora",                kind: "crt",    field: "carrier" },
  { idx: 31, name: "Data Prevista",                 kind: "detail", field: "expected_date" },
  { idx: 32, name: "Data Carregamento",             kind: "detail", field: "loading_date" },
  { idx: 33, name: "Data Descarregamento",          kind: "detail", field: "unloading_date", notes: "Formato M/D/YYYY (americano)" },
  { idx: 34, name: "Placa Carreta",                 kind: "detail", field: "trailer_plate" },
  { idx: 35, name: "Placa Bitrem",                  kind: "ignored" },
  { idx: 36, name: "Tipo Posição",                  kind: "detail", field: "position_type" },
  { idx: 37, name: "Qtd. dias armazenado",          kind: "detail", field: "storage_days" },
  { idx: 38, name: "Fórmula",                       kind: "detail", field: "formula" },
  { idx: 39, name: "Local Carga",                   kind: "crt",    field: "loading_location" },
  { idx: 40, name: "Cidade Carga",                  kind: "crt",    field: "loading_city" },
  { idx: 41, name: "País Carga",                    kind: "crt",    field: "loading_country" },
  { idx: 42, name: "Local Descarga",                kind: "crt",    field: "unloading_location" },
  { idx: 43, name: "Cidade Descarga",               kind: "crt",    field: "unloading_city" },
  { idx: 44, name: "País Descarga",                 kind: "crt",    field: "unloading_country" },
  { idx: 45, name: "Referência",                    kind: "crt",    field: "reference" },
  { idx: 46, name: "Fatura Comercial",              kind: "crt",    field: "commercial_invoice" },
  { idx: 47, name: "Valor Fob",                     kind: "crt",    field: "fob_value" },
  { idx: 48, name: "Aduana Origem Embarque",        kind: "crt",    field: "customs_origin" },
  { idx: 49, name: "Aduana Destino Embarque",       kind: "crt",    field: "customs_destination" },
  { idx: 50, name: "Nota/Danfe",                    kind: "crt",    field: "note_danfe" },
  { idx: 51, name: "Total Adto Motorista",          kind: "detail", field: "driver_advance_total" },
  { idx: 52, name: "Contrato Frete",                kind: "crt",    field: "freight_contract" },
  { idx: 53, name: "Frota / Freteiro",              kind: "crt",    field: "fleet_label" },
  { idx: 54, name: "Observação Controle Cargas",    kind: "crt",    field: "cargo_control_obs" },
  { idx: 55, name: "Nº Protocolo de Averbação",     kind: "crt",    field: "endorsement_protocol" },
  { idx: 56, name: "TIPO_CONSULTA",                 kind: "crt",    field: "query_type" },
  { idx: 57, name: "Danfe",                         kind: "crt",    field: "danfe" },
  { idx: 58, name: "Configuração Tipo Status",      kind: "crt",    field: "status_config" },
  { idx: 59, name: "Status Atual",                  kind: "detail", field: "current_status" },
  { idx: 60, name: "Data Agendamento Coleta",       kind: "detail", field: "collection_schedule_date" },
  { idx: 61, name: "Chegada em coleta",             kind: "detail", field: "collection_arrival" },
  { idx: 62, name: "Saida de coleta",               kind: "detail", field: "collection_departure" },
  { idx: 63, name: "Previsão de chegada fr br",     kind: "detail", field: "expected_arrival_border" },
  { idx: 64, name: "Prev. chegada aduana destino",  kind: "detail", field: "expected_customs_destination" },
  { idx: 65, name: "Entrada aduana origem exp",     kind: "detail", field: "customs_origin_entry", notes: "exp — prioritaria" },
  { idx: 66, name: "Saida aduana origem exp",       kind: "detail", field: "customs_origin_exit",  notes: "exp — prioritaria" },
  { idx: 67, name: "Entrada aduana transito",       kind: "detail", field: "customs_transit_entry" },
  { idx: 68, name: "Saida aduana transito",         kind: "detail", field: "customs_transit_exit" },
  { idx: 69, name: "Armazenado - 60 dias",          kind: "ignored" },
  { idx: 70, name: "Prev. chegada descarga",        kind: "detail", field: "expected_unloading_arrival" },
  { idx: 71, name: "Armazenado",                    kind: "ignored" },
  { idx: 72, name: "Entrada aduana destino exp",    kind: "detail", field: "customs_destination_entry", notes: "exp — prioritaria" },
  { idx: 73, name: "Saida da descarga",             kind: "detail", field: "unloading_departure" },
  { idx: 74, name: "Saida aduana destino exp",      kind: "detail", field: "customs_destination_exit", notes: "exp — prioritaria" },
  { idx: 75, name: "Armazenado - 45 dias",          kind: "ignored" },
  { idx: 76, name: "Chegada para descarga",         kind: "detail", field: "unloading_arrival" },
  { idx: 77, name: "Antecipação de senha",          kind: "detail", field: "password_anticipation" },
  { idx: 78, name: "Chegada em fronteira",          kind: "detail", field: "border_arrival" },
  { idx: 79, name: "Cruze autorizado",              kind: "detail", field: "authorized_crossing" },
  { idx: 80, name: "Solicitação de senha",          kind: "detail", field: "password_request" },
  // Cols 81-85: imp variants added back at the end in the latest ucarga update
  { idx: 81, name: "Entrada aduana origem imp",     kind: "ignored", notes: "imp — ignorada (col 65 exp tiene precedencia)" },
  { idx: 82, name: "Saida aduana origem imp",       kind: "ignored", notes: "imp — ignorada (col 66 exp tiene precedencia)" },
  { idx: 83, name: "Entrada aduana destino imp",    kind: "ignored", notes: "imp — ignorada (col 72 exp tiene precedencia)" },
  { idx: 84, name: "Saida aduana destino imp",      kind: "ignored", notes: "imp — ignorada (col 74 exp tiene precedencia)" },
  { idx: 85, name: "Armazenado - 50 dias",          kind: "ignored" },
]

const KIND_LABEL: Record<ColKind, string> = {
  crt:     "smart_gt_crt",
  detail:  "smart_gt_crt_details",
  ignored: "Ignorada",
  empty:   "—",
}

const KIND_STYLE: Record<ColKind, string> = {
  crt:     "bg-blue-50 dark:bg-blue-950/30",
  detail:  "bg-emerald-50 dark:bg-emerald-950/30",
  ignored: "bg-muted/40 text-muted-foreground",
  empty:   "bg-muted/20 text-muted-foreground",
}

function KindBadge({ kind }: { kind: ColKind }) {
  if (kind === "crt")
    return <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-800 hover:bg-blue-100">smart_gt_crt</Badge>
  if (kind === "detail")
    return <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">crt_details</Badge>
  if (kind === "ignored")
    return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">ignorada</Badge>
  return <span className="text-muted-foreground text-xs">—</span>
}

// ─── Summary counts ───────────────────────────────────────────────────────────

const saved    = COLUMNS.filter(c => c.kind === "crt" || c.kind === "detail").length
const ignoredN = COLUMNS.filter(c => c.kind === "ignored").length
const crtN     = COLUMNS.filter(c => c.kind === "crt").length
const detailN  = COLUMNS.filter(c => c.kind === "detail").length

export default function SmartGtV2FormatPage() {
  return (
    <>
      <SiteHeader title="Formato SmartGT v2 — Referencia de columnas" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-6">

          {/* ─── Breadcrumb ─── */}
          <Link
            to="/docs"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeftIcon className="size-3" /> Documentación
          </Link>

          {/* ─── Intro ─── */}
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Formato SmartGT v2 — Referencia de columnas</h2>
            <p className="text-sm text-muted-foreground">
              Mapeo completo de las <strong>86 columnas</strong> del archivo XLS exportado por ucarga en formato v2.
              Muestra qué tabla recibe cada columna y cuáles son ignoradas por el parser.
            </p>
          </div>

          {/* ─── Summary ─── */}
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-lg border px-4 py-3 flex flex-col gap-0.5 min-w-28">
              <span className="text-2xl font-bold">{COLUMNS.length}</span>
              <span className="text-xs text-muted-foreground">columnas totales</span>
            </div>
            <div className="rounded-lg border px-4 py-3 flex flex-col gap-0.5 min-w-28 bg-blue-50 dark:bg-blue-950/30">
              <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">{crtN}</span>
              <span className="text-xs text-muted-foreground">→ smart_gt_crt</span>
            </div>
            <div className="rounded-lg border px-4 py-3 flex flex-col gap-0.5 min-w-28 bg-emerald-50 dark:bg-emerald-950/30">
              <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{detailN}</span>
              <span className="text-xs text-muted-foreground">→ crt_details</span>
            </div>
            <div className="rounded-lg border px-4 py-3 flex flex-col gap-0.5 min-w-28">
              <span className="text-2xl font-bold text-muted-foreground">{ignoredN}</span>
              <span className="text-xs text-muted-foreground">ignoradas</span>
            </div>
            <div className="rounded-lg border px-4 py-3 flex flex-col gap-0.5 min-w-28">
              <span className="text-2xl font-bold">{saved}</span>
              <span className="text-xs text-muted-foreground">guardadas en DB</span>
            </div>
          </div>

          {/* ─── Leyenda ─── */}
          <div className="rounded-lg border bg-muted/20 px-4 py-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Leyenda:</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 border border-blue-200" />
              Guardada en <code className="font-mono">smart_gt_crt</code>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-200" />
              Guardada en <code className="font-mono">smart_gt_crt_details</code>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-muted border" />
              Ignorada — no se guarda en DB
            </span>
          </div>

          {/* ─── Nota v2 ─── */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-xs text-amber-800 dark:text-amber-300 space-y-1">
            <p className="font-semibold">Diferencias clave respecto a v1</p>
            <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-400">
              <li>Nueva columna en índice 14: <code className="font-mono">RG / C.I Motorista</code> — desplaza todas las columnas ≥ 14 en +1.</li>
              <li>Las variantes <code className="font-mono">imp</code> de aduana se eliminaron del bloque central y se reubicaron al final (cols 81–84) en la última actualización de ucarga.</li>
              <li>El parser usa las variantes <code className="font-mono">exp</code> (cols 65/66/72/74) como fuente principal. Las <code className="font-mono">imp</code> al final son ignoradas.</li>
            </ul>
          </div>

          {/* ─── Table ─── */}
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-10">#</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Nombre columna (ucarga)</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-32">Destino</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Campo DB</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Notas</th>
                </tr>
              </thead>
              <tbody>
                {COLUMNS.map((col) => (
                  <tr key={col.idx} className={cn("border-b last:border-0", KIND_STYLE[col.kind])}>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">{col.idx}</td>
                    <td className="px-3 py-1.5 font-medium">
                      {col.kind === "empty"
                        ? <span className="text-muted-foreground italic">{col.name}</span>
                        : col.name
                      }
                    </td>
                    <td className="px-3 py-1.5">
                      <KindBadge kind={col.kind} />
                    </td>
                    <td className="px-3 py-1.5">
                      {col.field
                        ? <code className="font-mono text-[11px] bg-black/5 dark:bg-white/10 px-1 py-0.5 rounded">{col.field}</code>
                        : <span className="text-muted-foreground">—</span>
                      }
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">{col.notes ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </>
  )
}
